import gql from "graphql-tag";
import * as Cookies from "es-cookie";

import client from "../apolloClient";

/**
 * Keeps the `isStudentUserValid` check fresh without every component asking for
 * it separately.
 *
 * 52 components register a `graphql(isStudentUserValid)` HOC, and one of them is
 * AsideNavbar, which is mounted in 177 files. Every page therefore ran the query
 * at least twice — once for the sidebar, once for the page, sometimes again for
 * a section component — and all of them used `network-only` or
 * `cache-and-network`, so each was a real round trip.
 *
 * Those call sites are now `cache-first`. They all request the same root field
 * with the same argument, so Apollo stores them in a single normalized entry and
 * the first component to mount is the only one that fetches; the rest read what
 * is already there.
 *
 * That alone would be wrong, because this is not reference data. The resolver
 * reports `estatus` and a device mismatch, and pages force a logout when
 * `estatus == 0` — it is how "someone logged into your account elsewhere" gets
 * enforced. Caching it for a whole session would quietly switch that off. So
 * this module re-runs the query on navigation and writes the fresh result into
 * that same cache entry, which Apollo then broadcasts to every mounted watcher.
 *
 * The query below selects the union of the fields those 52 documents ask for, so
 * a revalidation refreshes all of them rather than leaving some stale.
 */

export const SESSION_VALIDITY = gql`
  query($student_user_exam_id: Int!) {
    isStudentUserValid(student_user_exam_id: $student_user_exam_id) {
      estatus
      current_plan_id
      expiry_date
      app_version
      user_access_restictions
      module_restrictions
      chapter_ids
      previous_sets
      isTrialUser
    }
  }
`;

/**
 * Route changes come in bursts — a redirect chain fires several within a few
 * hundred milliseconds — and a student clicking through the sidebar can produce
 * a navigation a second. Without a floor this would be no better than the
 * per-mount fetching it replaces.
 */
const MIN_INTERVAL_MS = 30000;

let lastCheckedAt = 0;
let inFlight = null;
/**
 * Who the last check was for. Logout navigates with `history.push`, so this
 * module survives it; without the comparison below, a second student logging in
 * on the same tab within the interval would be waved through on the previous
 * student's result.
 */
let lastCheckedFor = null;

function currentStudentExamId() {
  const id = parseInt(Cookies.get("student_user_exam_id"), 10);
  return isNaN(id) ? null : id;
}

/**
 * Refetch unless it was done recently. Call freely — on every route change, from
 * anywhere. Concurrent callers share the one request.
 *
 * @param {boolean} [force]  ignore the interval, e.g. when returning to a tab.
 * @returns {Promise} resolves when the cache has been updated; never rejects.
 */
export function revalidateSessionValidity(force) {
  const studentUserExamId = currentStudentExamId();

  // Logged out, or still registering. Asking would only produce an
  // UNAUTHENTICATED bounce back to login.
  if (studentUserExamId === null || !Cookies.get("studenttoken")) {
    return Promise.resolve(null);
  }

  if (inFlight) return inFlight;

  const now = new Date().getTime();
  const sameStudent = studentUserExamId === lastCheckedFor;
  if (!force && sameStudent && now - lastCheckedAt < MIN_INTERVAL_MS) {
    return Promise.resolve(null);
  }
  lastCheckedAt = now;
  lastCheckedFor = studentUserExamId;

  inFlight = client
    .query({
      query: SESSION_VALIDITY,
      variables: { student_user_exam_id: studentUserExamId },
      // Writes through to the shared cache entry, so the cache-first HOCs on
      // screen re-render with the new estatus rather than their stale copy.
      fetchPolicy: "network-only"
    })
    .then(result => {
      inFlight = null;
      return (result.data && result.data.isStudentUserValid) || null;
    })
    .catch(error => {
      // Never surfaced to the user: the components watching this query still
      // show whatever the cache holds, and an UNAUTHENTICATED failure is
      // already handled by the error link in apolloClient.js.
      console.error("isStudentUserValid revalidation failed", error);
      inFlight = null;
      // Allow the next navigation to try again rather than waiting out the
      // interval on a result we never got.
      lastCheckedAt = 0;
      return null;
    });

  return inFlight;
}

/** Forget when we last checked, so the next call goes to the network. */
export function resetSessionValidity() {
  lastCheckedAt = 0;
  lastCheckedFor = null;
  inFlight = null;
}

export default revalidateSessionValidity;
